import { useState } from 'react';

import { Badge, Button, Card, CardContent, CardFooter, CardHeader, CardTitle } from '@govard/ui-primitives';
import { useTranslation } from '@govard/i18n-core/react';

import { useApprovalMutation } from '../hooks/useApprovalMutation.js';
import type { ApprovalItem } from '../api/approvals.client.js';

interface AiApprovalCardProps {
  approval: ApprovalItem;
  summary: string;
  diff: string;
}

export function AiApprovalCard({ approval, summary, diff }: AiApprovalCardProps) {
  const { t } = useTranslation();
  const approvalMutation = useApprovalMutation();
  const [resolvedApproval, setResolvedApproval] = useState<ApprovalItem | null>(approval.status === 'pending' ? null : approval);

  async function handleDecision(decision: 'approve' | 'reject') {
    const updatedApproval = await approvalMutation.mutateAsync({ approvalId: approval.id, decision });
    setResolvedApproval(updatedApproval);
  }

  const currentApproval = resolvedApproval ?? approval;
  const isResolved = currentApproval.status !== 'pending';
  const resolutionLabel = currentApproval.status === 'approved' ? t('crm.approvals.approved') : t('crm.approvals.rejected');

  return (
    <Card data-testid={`approval-card-${approval.id}`}>
      <CardHeader>
        <CardTitle>{summary}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-sm text-muted-foreground">{diff}</p>
        <p className="text-xs text-muted-foreground">
          {t('crm.approvals.expires_at')} {new Date(currentApproval.expiresAt).toLocaleString()}
        </p>
      </CardContent>
      <CardFooter className="gap-3">
        {isResolved ? (
          <Badge data-testid="chat-approval-status" variant={currentApproval.status === 'approved' ? 'success' : 'outline'}>
            {resolutionLabel}
          </Badge>
        ) : (
          <>
            <Button data-testid="chat-approval-reject" disabled={approvalMutation.isPending} onClick={() => void handleDecision('reject')} variant="outline">
              {t('crm.approvals.reject')}
            </Button>
            <Button data-testid="chat-approval-approve" disabled={approvalMutation.isPending} onClick={() => void handleDecision('approve')} variant="primary">
              {t('crm.approvals.approve')}
            </Button>
          </>
        )}
      </CardFooter>
    </Card>
  );
}
